import React, { useState, useEffect, ReactNode } from 'react'
import { useRouter } from 'next/router'
import { isBrowser, checkStoreIs, getAccountPageUrl } from '@helpers/env'
import Translations from '@helpers/translations'
import { ReturnsHeaderImage } from '@components/returns/ReturnsHeaderImage'
import Notification from '@components/ui/Notification'
import { SubmitButton } from '@components/ui/Button'
import styles from './styles.module.scss'
import Link from '@components/ui/Link'
import { ItemSelectionOnSubmitProps } from '../ItemsSelection'
import { InputField } from '@components/ui/formElements/form/InputField'
import { FormProvider, useForm } from 'react-hook-form'
import FormGrid from '@components/ui/formElements/form/shared/FormGrid'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'

type HeaderType = {
  title: string
  byline: string[]
  image: {
    mobile: string
    desktop: string
  }
}

type ContentType = {
  id: string
  question: string
  type: string
  required: boolean
  answers: string[]
  inputMode?: string
  pattern?: string
  hidden: boolean
}

export type FormData = {
  errorNotification: ReactNode
  header: HeaderType
  buttonCopy: string
  content: ContentType[]
}

type OrderFormProps = {
  formData: FormData
  onSubmit: (args: ItemSelectionOnSubmitProps) => void
}

function transformOrderNumber(orderNumber: string) {
  const isNonGBStore = checkStoreIs('eu') || checkStoreIs('us')

  if (
    orderNumber &&
    isNonGBStore &&
    !orderNumber.toUpperCase().includes(process.env.SHOP_LOCALE)
  ) {
    return `${process.env.SHOP_LOCALE}${orderNumber}`
  }

  return orderNumber.toUpperCase()
}

const ReturnsLoginSchema = z.object({
  order_number: z.string().min(1, { message: 'Field cannot be empty' }),
  postcode: z.string().min(1, { message: 'Field cannot be empty' })
})

export type ReturnsLoginData = z.infer<typeof ReturnsLoginSchema>

function OrderForm({ formData, onSubmit }: OrderFormProps) {
  const router = useRouter()
  const { error } = router.query
  const methods = useForm<ReturnsLoginData>({
    mode: 'onBlur',
    shouldFocusError: true,
    resolver: zodResolver(ReturnsLoginSchema),
    defaultValues: {
      order_number: '',
      postcode: ''
    }
  })

  const [buttonState, setButtonState] = useState<
    'loading' | 'ready' | 'disabled'
  >('disabled')

  const { formState } = methods
  const { isValid, isDirty } = formState

  useEffect(() => {
    setButtonState(isValid && isDirty ? 'ready' : 'disabled')
  }, [isValid, isDirty])

  const handleOnSubmit = (ev: React.FormEvent<HTMLFormElement>) => {
    ev.preventDefault()
    setButtonState('loading')

    const values = methods.getValues()
    const normaliseOrderNumber = {
      ...values,
      order_number: transformOrderNumber(values.order_number)
    }

    onSubmit({
      queryParams: {
        ...normaliseOrderNumber
      }
    } as ItemSelectionOnSubmitProps)
  }

  return (
    isBrowser() && (
      <>
        <ReturnsHeaderImage content={formData.header} />
        {error && (
          <div className={styles.errorContainer}>
            <Notification
              type="error"
              content={formData.errorNotification}
              classes={styles.errorContainer__error}
            />
          </div>
        )}
        <FormProvider {...methods}>
          <form className={styles.form} onSubmit={handleOnSubmit} noValidate>
            <FormGrid>
              {formData.content.map(data => (
                <InputField
                  key={data.id}
                  id={data.id}
                  name={data.id}
                  label={`${data.question}*`}
                  placeholder={`${data.question}*`}
                />
              ))}
            </FormGrid>
            <div className={styles.form__button}>
              <SubmitButton
                type="submit"
                fullWidth="always"
                state={buttonState}
                disabled={!isValid || !isDirty}
              >
                {formData.buttonCopy}
              </SubmitButton>
            </div>
            <p className={styles.form__preOrderLink}>
              {Translations.localise('common.form.preorder')}
              <Link href={getAccountPageUrl('pages/my-pre-orders')}>
                {Translations.localise('common.ctas.click-here')}
              </Link>
            </p>
          </form>
        </FormProvider>
      </>
    )
  )
}

export default OrderForm
